"use client";

import type { CampaignClaim, CampaignMetadataHttp, CampaignSummary } from "@decibeltrade/sdk";
import { useQueries } from "@tanstack/react-query";

import { campaignTypeTraits, type CampaignVisibility } from "@/app/(main)/rewards/category-keys";
import type { Reward, RewardsList, RewardStatus } from "@/app/(main)/rewards/types";
import { activeCampaignsQuery } from "@/contexts/queries/campaigns/use-active-campaigns";
import { campaignSummaryQuery } from "@/contexts/queries/campaigns/use-campaign-summary";
import { chainUnitsToAmount } from "@/utils/math";
import { getServerTime, useServerTimeDelta } from "@/utils/server-time";

const EMPTY_COUNTS: Record<RewardStatus, number> = {
  claimable: 0,
  in_progress: 0,
  not_eligible: 0,
  claimed: 0,
  expired: 0,
};

function activeCampaignAsClaim(c: CampaignMetadataHttp): CampaignClaim {
  return {
    campaignId: c.campaignId,
    campaignType: c.campaignType,
    status: c.status,
    title: c.title,
    rewardAsset: c.rewardAsset,
    startTsSec: c.startTsSec,
    endTsSec: c.endTsSec,
    claimStartTsSec: c.claimStartTsSec,
    claimEndTsSec: c.claimEndTsSec,
    totalFunded: c.totalFunded,
    description: c.description,
    hasAllocation: false,
    claimableAmount: 0,
    claimedAmount: 0,
    readyToClaim: 0,
    claimedAtTsSec: 0,
    claimTxHash: "",
  };
}

function deriveStatus(claim: CampaignClaim, nowSec: number): RewardStatus | null {
  // Claimed wins over expiry: a user who claimed during Active and then sees the
  // campaign expire shouldn't be told "Claim window has closed".
  if (claim.claimableAmount > 0 && claim.claimedAmount >= claim.claimableAmount) {
    return "claimed";
  }

  if (claim.status === "expired" || claim.status === "reclaimed" || claim.status === "cancelled") {
    return "expired";
  }
  if (claim.status === "active") {
    if (!claim.hasAllocation) {
      return "not_eligible";
    }
    if (nowSec > claim.claimEndTsSec) {
      return "expired";
    }
    if (nowSec >= claim.claimStartTsSec) {
      // Show in_progress (not claimable) between vest ticks to avoid a $0 claim CTA.
      return claim.readyToClaim > 0 ? "claimable" : "in_progress";
    }
    // Pre-claim window: hide until claimStartTsSec.
    return null;
  }
  return null;
}

// claimable: amount about to land; claimed: amount that already landed; else: total allocation.
function headlineAmount(claim: CampaignClaim, status: RewardStatus): number {
  switch (status) {
    case "claimable":
      return claim.readyToClaim;
    case "claimed":
      return claim.claimedAmount;
    case "in_progress":
    case "not_eligible":
    case "expired":
      return claim.claimableAmount;
  }
}

function buildReward(claim: CampaignClaim, nowSec: number): Reward | null {
  const status = deriveStatus(claim, nowSec);
  if (!status) {
    return null;
  }
  const base = {
    id: claim.campaignId,
    campaignType: claim.campaignType,
    category: campaignTypeTraits(claim.campaignType).category,
    title: claim.title,
    description: claim.description,
    rewardAmountUsd: chainUnitsToAmount(headlineAmount(claim, status)),
    claimEndTsSec: claim.claimEndTsSec,
  } as const;

  if (status === "claimed") {
    return {
      ...base,
      status,
      claimedAmountUsd: chainUnitsToAmount(claim.claimedAmount),
      claimedAtTsSec: claim.claimedAtTsSec ?? 0,
      claimTxHash: claim.claimTxHash || null,
    };
  }
  return { ...base, status };
}

function isListed(visibility: CampaignVisibility, hasAllocation: boolean): boolean {
  switch (visibility) {
    case "hidden":
      return false;
    case "recipient_only":
      return hasAllocation;
    case "public":
      return true;
  }
}

function deriveList(
  ownerAddr: string | undefined,
  userData: CampaignSummary | undefined,
  activeData: CampaignMetadataHttp[] | undefined,
): RewardsList {
  if (ownerAddr && !userData) {
    return { rewards: [], counts: { ...EMPTY_COUNTS } };
  }
  const nowSec = Math.floor(getServerTime() / 1000);
  const claims = userData?.claims ?? [];
  const active = activeData ?? [];

  const seen = new Set(claims.map((c) => c.campaignId));
  const merged: CampaignClaim[] = [
    ...claims,
    ...active.filter((c) => !seen.has(c.campaignId)).map(activeCampaignAsClaim),
  ];

  // FFT renders via FftRewardCard. Recipient-only is a UI choice; the data is public on-chain.
  const rewards = merged
    .filter((c) => isListed(campaignTypeTraits(c.campaignType).visibility, c.hasAllocation))
    .map((c) => buildReward(c, nowSec))
    .filter((r): r is Reward => r !== null);
  const counts = { ...EMPTY_COUNTS };
  for (const r of rewards) {
    counts[r.status] += 1;
  }
  return { rewards, counts };
}

/**
 * Merges the user's claims with active campaigns the user has no row in yet and
 * derives a per-row `RewardStatus`. There is no single SDK endpoint that returns
 * this union, so the standard key/query/suspense/conditional shape from
 * `web-conventions.md` doesn't apply — `useQueries` is the React Query primitive
 * for "two sources, one merged view".
 */
export function useRewardsList(ownerAddr: string | undefined) {
  useServerTimeDelta();
  return useQueries({
    queries: [
      { ...campaignSummaryQuery({ accountAddress: ownerAddr }), refetchInterval: 300_000 },
      activeCampaignsQuery(),
    ],
    combine: ([userQ, activeQ]) => ({
      data: deriveList(ownerAddr, userQ.data, activeQ.data),
      isLoading: (!!ownerAddr && userQ.isLoading) || activeQ.isLoading,
      // activeQ failure degrades to "user's own claims only" — non-fatal.
      isError: userQ.isError,
      refetch: async () => {
        await Promise.all([userQ.refetch(), activeQ.refetch()]);
      },
    }),
  });
}
